'use client';

import { SvgLeft, SvgRight, SvgZoom } from '@/components/icons-eataly';
import { cn } from '@/lib/utils';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import type { ShopperProducts } from '@/scapi';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PdpWishlistButton } from '@/components/buttons/pdp-wishlist-button';
import { ImageGalleryZoomDialog } from './image-gallery-zoom-dialog';
import type { GalleryImage } from './types';
import { Button } from '../ui/button';
import DynamicImage from '@/components/dynamic-image';
import {
    IMAGE_GALLERY_MAIN_HEIGHTS,
    IMAGE_GALLERY_MAIN_WIDTHS,
    IMAGE_GALLERY_THUMBNAIL_WIDTHS,
} from '@/lib/images/gallery-sizes';

export type { GalleryImage } from './types';

interface ImageGalleryProps {
    images: GalleryImage[];
    eager?: boolean;
    /** Show prev/next arrows on the main image (e.g. in modal) */
    showNavigationArrows?: boolean;
    /** Size of navigation arrows: "sm" (default) or "lg" for PDP */
    navigationArrowSize?: 'sm' | 'lg';
    /** Use horizontal scrollable thumbnail strip with arrows instead of grid */
    horizontalThumbnails?: boolean;
    productName?: string;
    product: ShopperProducts.schemas['Product'];
}

export default function ImageGallery({ images, eager = false, productName, product }: ImageGalleryProps): ReactElement {
    const { t: tCommon } = useTranslation('common');
    const { t: tProduct } = useTranslation('product');
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showDesktopArrows, setShowDesktopArrows] = useState({
        left: false,
        right: product.imageGroups?.[0]?.images && product.imageGroups?.[0]?.images?.length > 5,
    });
    const [zoomOpen, setZoomOpen] = useState(false);
    const [thumbEmblaRef, thumbEmblaApi] = useEmblaCarousel({
        align: 'center',
        containScroll: 'trimSnaps',
        dragFree: false,
    });
    const thumbDesktopRef = useRef<HTMLDivElement>(null);
    const sentinelLeftRef = useRef<HTMLButtonElement>(null);
    const sentinelRightRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!thumbEmblaApi) return;
        thumbEmblaApi?.scrollTo(selectedImageIndex, true);
    }, [thumbEmblaApi, selectedImageIndex]);

    useEffect(() => {
        thumbEmblaApi?.reInit();
    }, [thumbEmblaApi, images.length]);

    useEffect(() => {
        if (!thumbEmblaApi) return;
        const syncSelectionFromCarousel = () => {
            const snap = thumbEmblaApi.selectedScrollSnap();
            setSelectedImageIndex((prev) => (prev === snap ? prev : snap));
        };
        thumbEmblaApi.on('select', syncSelectionFromCarousel);
        thumbEmblaApi.on('reInit', syncSelectionFromCarousel);
        syncSelectionFromCarousel();
        return () => {
            thumbEmblaApi.off('select', syncSelectionFromCarousel);
            thumbEmblaApi.off('reInit', syncSelectionFromCarousel);
        };
    }, [thumbEmblaApi]);

    useEffect(() => {
        setSelectedImageIndex((currentIndex) => {
            return currentIndex < images.length ? currentIndex : 0;
        });
        const options = {
            root: thumbDesktopRef.current,
            threshold: 0.1,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === sentinelLeftRef.current)
                    setShowDesktopArrows((prev) => ({ ...prev, left: !entry.isIntersecting }));
                if (entry.target === sentinelRightRef.current)
                    setShowDesktopArrows((prev) => ({ ...prev, right: !entry.isIntersecting }));
            });
        }, options);

        if (sentinelLeftRef.current) observer.observe(sentinelLeftRef.current);
        if (sentinelRightRef.current) observer.observe(sentinelRightRef.current);

        return () => observer.disconnect();
    }, [images]);

    if (!images || images.length === 0) {
        return (
            <div className="space-y-4">
                <div className="bg-muted flex aspect-square items-center justify-center rounded-lg">
                    <div className="text-neutral-20 text-center">
                        <p>{tCommon('noImageAvailable')}</p>
                    </div>
                </div>
                <div className="flex items-center justify-end max-lg:px-1 lg:hidden">
                    <PdpWishlistButton product={product} size="sm" />
                </div>
            </div>
        );
    }

    const imageAltFallback = resolveImageAlt({ name: productName, fallback: tProduct('imageAlt') || 'Product Image' });

    return (
        <>
            <div className="space-y-4">
                {/* Main Image */}
                {images.length && (
                    <div className="space-y-3">
                        <div
                            className={cn(
                                'overflow-hidden',
                                // Gallery column is `max-lg:px-6`. Pull the track through that
                                // right padding so the next slide reaches the viewport edge.
                                images.length > 1 && 'max-md:-mr-6'
                            )}
                            ref={thumbEmblaRef}>
                            <div className="flex gap-2 sm:gap-3">
                                {images.map((image, index) => (
                                    <div
                                        key={image.src + (image.thumbSrc || '')}
                                        className={cn(
                                            'bg-muted relative aspect-square shrink-0 overflow-hidden rounded-lg',
                                            images.length === 1
                                                ? 'flex-[0_0_100%]'
                                                : 'flex-[0_0_calc(100%-60px-1.5rem)] md:flex-[0_0_100%]',
                                            // Embla reads the last slide's end margin as the scroll end inset.
                                            images.length > 1 && index === images.length - 1 && 'max-md:mr-6',
                                            selectedImageIndex === index
                                                ? 'border-primary'
                                                : 'hover:border-border border-transparent'
                                        )}>
                                        <DynamicImage
                                            src={image.src}
                                            alt={resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })}
                                            className="h-full w-full"
                                            loading={eager && index === 0 ? undefined : 'lazy'}
                                            priority={eager && index === 0 ? 'high' : 'auto'}
                                            widths={IMAGE_GALLERY_MAIN_WIDTHS}
                                            heights={IMAGE_GALLERY_MAIN_HEIGHTS}
                                            imageProps={{
                                                className: 'h-full w-full object-cover object-center',
                                            }}
                                        />
                                        <button
                                            type="button"
                                            aria-label={tProduct('zoomIn')}
                                            onClick={() => setZoomOpen(true)}
                                            className="bg-neutral-white absolute right-2 bottom-2 flex h-12 w-12 items-center justify-center rounded-full max-md:hidden">
                                            <SvgZoom aria-hidden="true" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {/* Thumbnail Navigation — Embla: 1 slide + bullets on mobile, 5 slides on sm+ */}
                <div className="flex items-center justify-between max-lg:px-1">
                    <button
                        type="button"
                        aria-label={tProduct('zoomIn')}
                        onClick={() => setZoomOpen(true)}
                        className="lg:hidden">
                        <SvgZoom aria-hidden="true" />
                    </button>
                    <div className="relative flex items-center lg:w-full">
                        {images.length > 1 && (
                            <>
                                {/* `gap-1` + `p-0.5` per slot = the 8px spacing from the design, with the
                                    2px the active outline needs so the scroll container can't clip it. */}
                                <div
                                    className="relative flex items-center justify-center md:flex md:justify-start overflow-x-auto [scrollbar-width:none] gap-1 snap-x snap-mandatory lg:w-full"
                                    ref={thumbDesktopRef}>
                                    {images.map((image, index) => {
                                        const isFirst = index === 0;
                                        const isLast = index === images.length - 1;
                                        return (
                                            <button
                                                key={image.src + (image.thumbSrc || '')}
                                                onClick={() => setSelectedImageIndex(index)}
                                                ref={isFirst ? sentinelLeftRef : isLast ? sentinelRightRef : undefined}
                                                className={cn(
                                                    'lg:basis-[calc((100%-16px)/5)] lg:flex-shrink-0 lg:p-0.5 lg:snap-start cursor-pointer'
                                                )}>
                                                <div
                                                    className={cn(
                                                        'aspect-square overflow-hidden rounded-md max-lg:hidden outline-1 outline-offset-1 outline-transparent',
                                                        selectedImageIndex === index && 'outline-brown-30'
                                                    )}>
                                                    <DynamicImage
                                                        src={image.src}
                                                        alt={resolveImageAlt({
                                                            alt: image.alt,
                                                            fallback: imageAltFallback,
                                                        })}
                                                        className="h-full w-full max-lg:hidden"
                                                        loading="lazy"
                                                        widths={IMAGE_GALLERY_THUMBNAIL_WIDTHS}
                                                        imageProps={{
                                                            className: 'h-full w-full object-cover object-center',
                                                        }}
                                                    />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        {showDesktopArrows.left && (
                            <Button
                                type="button"
                                className="flex-shrink-0 mx-1 absolute left-0 z-1 max-lg:hidden"
                                onClick={() => thumbDesktopRef.current?.scrollBy({ left: -100, behavior: 'smooth' })}
                                iconOnly
                                aria-label={tProduct('nextImage')}>
                                <SvgLeft className="size-6" aria-hidden />
                            </Button>
                        )}
                        {showDesktopArrows.right && (
                            <Button
                                type="button"
                                className="flex-shrink-0 mx-1 absolute right-0 max-lg:hidden"
                                onClick={() => thumbDesktopRef.current?.scrollBy({ left: 100, behavior: 'smooth' })}
                                iconOnly
                                aria-label={tProduct('nextImage')}>
                                <SvgRight className="size-6" aria-hidden />
                            </Button>
                        )}
                    </div>
                    <PdpWishlistButton product={product} size="sm" className="lg:hidden" />
                </div>
            </div>

            <ImageGalleryZoomDialog
                open={zoomOpen}
                onOpenChange={setZoomOpen}
                images={images}
                imageAltFallback={imageAltFallback}
                initialIndex={selectedImageIndex}
                onSyncSelection={setSelectedImageIndex}
                title={tProduct('imageViewerTitle')}
            />
        </>
    );
}
